import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react'
import { useId } from 'react'
import { uiScope } from '@/lib/cssScope'
import styles from '@/styles/components/ui/TextField.module.scss'

export interface TextFieldProps {
  value: string
  onChange: (value: string) => void
  /** Uppercase label above the field (same rhythm as Select). */
  label?: ReactNode
  placeholder?: string
  disabled?: boolean
  className?: string
  minWidth?: string
  id?: string
  type?: React.HTMLInputTypeAttribute
  name?: string
  autoComplete?: string
  'aria-label'?: string
  readOnly?: boolean
  maxLength?: number
  /** Use multiline textarea instead of input. */
  multiline?: boolean
  rows?: number
  /** Monospace font for JSON / code. */
  monospace?: boolean
  /** For `type="number"` and similar. */
  min?: number | string
  max?: number | string
  step?: number | string
  required?: boolean
  /** Red border when validation failed. */
  invalid?: boolean
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

/**
 * Single-line or multiline text control with the same shell as Select (border, radius, focus ring).
 */
export function TextField({
  label,
  value,
  onChange,
  placeholder = '',
  disabled = false,
  className = '',
  minWidth,
  id,
  type = 'text',
  multiline = false,
  rows = 4,
  monospace = false,
  autoComplete,
  name,
  readOnly,
  maxLength,
  min,
  max,
  step,
  required,
  invalid = false,
  onKeyDown,
  'aria-label': ariaLabel,
}: TextFieldProps) {
  const uid = useId()
  const inputId = id ?? `${uid}-field`
  const labelId = label ? `${inputId}-label` : undefined

  const root = [uiScope('text-field'), styles.root, className].filter(Boolean).join(' ')

  const fieldClass = [
    styles.field,
    multiline ? styles.fieldMultiline : '',
    disabled ? styles.fieldDisabled : '',
    invalid ? styles.fieldInvalid : '',
  ]
    .filter(Boolean)
    .join(' ')

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleTextarea = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value)
  }

  return (
    <div className={root} style={minWidth ? { minWidth } : undefined}>
      {label != null && label !== false ? (
        <label id={labelId} htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      ) : null}
      <div className={fieldClass}>
        {multiline ? (
          <textarea
            id={inputId}
            name={name}
            className={`${styles.textarea} ${monospace ? styles.textareaMono : ''}`.trim()}
            value={value}
            onChange={handleTextarea}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            rows={rows}
            maxLength={maxLength}
            autoComplete={autoComplete}
            aria-labelledby={label ? labelId : undefined}
            aria-label={ariaLabel}
            onKeyDown={onKeyDown}
          />
        ) : (
          <input
            id={inputId}
            type={type}
            name={name}
            className={styles.input}
            value={value}
            onChange={handleInput}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            maxLength={maxLength}
            min={min}
            max={max}
            step={step}
            required={required}
            autoComplete={autoComplete}
            aria-labelledby={label ? labelId : undefined}
            aria-label={ariaLabel}
            onKeyDown={onKeyDown}
          />
        )}
      </div>
    </div>
  )
}
